import React, { useState, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Zap } from 'react-feather';
import { ConfigToolbar, ToolbarButton, ConfigEmptyState } from '../../components/ConfigPages';
import { InlineSelect, InlineCheckbox, BulkActionBar, StatusDot, SortIcon } from '../../components/InlineTable';
import { createLaborCoaMapping, updateLaborCoaMapping, deleteLaborCoaMapping, bulkUpsertLaborCoaMappings } from '../../utils/departmentApi';

function LaborRow({ position, departmentName, accountOptions, accountLandsIn, mapping, selected, onToggle, onChange }) {
  const [hov, setHov] = useState(false);
  const act = hov || selected;
  const value = mapping?.chartOfAccountId ?? null;
  const acct = value != null ? accountOptions.find((o) => o.value === value) : null;
  return (
    <tr className={`cp-row${selected ? ' it-row-selected' : ''}`} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
      <td style={{ width: 44 }} className="cp-cell">
        <InlineCheckbox checked={selected} onChange={() => onToggle(position.id)} />
      </td>
      <td style={{ minWidth: 200 }}>
        <div className="cp-cell">
          <div className="cp-name">{position.name}</div>
          {position.code ? <div className="cp-code">{position.code}</div> : null}
        </div>
      </td>
      <td style={{ minWidth: 160 }}>
        <div className="cp-cell" style={{ fontSize: 13, color: departmentName ? 'var(--lodge-onyx-600)' : 'var(--lodge-onyx-300)', fontStyle: departmentName ? 'normal' : 'italic' }}>
          {departmentName || 'No department'}
        </div>
      </td>
      <td style={{ minWidth: 340 }}>
        <InlineSelect value={value} options={accountOptions} onChange={onChange} placeholder="GL expense account…" rowActive={act} minWidth={360} ariaLabel="GL expense account" />
        {acct && accountLandsIn(value) ? <div className="cp-lands">lands in <strong>{accountLandsIn(value)}</strong></div> : null}
      </td>
      <td style={{ width: 56, textAlign: 'center' }}>
        <StatusDot state={value != null ? 'complete' : 'none'} title={value != null ? `Mapped to ${acct?.label || value}` : 'No GL account — labor cost is not on the P&L for this location'} />
      </td>
    </tr>
  );
}

/**
 * Labor GL tab — per-location position → GL expense account mapping.
 * The location comes from the page's single `selectedLocationId` (scope select in the head).
 */
export default function LaborGlTab({
  orgId, locationId, locationName, loading,
  positions, departments,
  accounts,            // expense COA rows for the location
  accountOptions,      // [{value,label}]
  mappings, setMappings,
  status, setStatus,
  onOpenAi,
}) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'position', direction: 'ascending' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);

  const live = useMemo(() => positions.filter((p) => !p.isDeleted), [positions]);
  const byPosition = useMemo(() => new Map(mappings.map((m) => [m.positionId, m])), [mappings]);
  const deptName = (id) => departments.find((d) => d.id === id)?.name || '';
  const acctLabel = (id) => accountOptions.find((o) => o.value === id)?.label || '';
  const accountLandsIn = (id) => {
    const a = accounts.find((x) => x.id === id);
    if (!a) return null;
    return [a.department, a.accountType].filter(Boolean).join(' · ') || null;
  };

  const counts = useMemo(() => {
    const mapped = live.filter((p) => byPosition.get(p.id)?.chartOfAccountId != null).length;
    return { all: live.length, mapped, unmapped: live.length - mapped };
  }, [live, byPosition]);

  const rows = useMemo(() => {
    let out = live;
    if (status === 'mapped') out = out.filter((p) => byPosition.get(p.id)?.chartOfAccountId != null);
    else if (status === 'unmapped') out = out.filter((p) => byPosition.get(p.id)?.chartOfAccountId == null);
    if (q) {
      const n = q.toLowerCase();
      out = out.filter((p) => p.name?.toLowerCase().includes(n) || p.code?.toLowerCase().includes(n)
        || deptName(p.departmentId).toLowerCase().includes(n) || acctLabel(byPosition.get(p.id)?.chartOfAccountId).toLowerCase().includes(n));
    }
    const val = (p) => (sort.key === 'account' ? acctLabel(byPosition.get(p.id)?.chartOfAccountId).toLowerCase()
      : sort.key === 'department' ? deptName(p.departmentId).toLowerCase() : (p.name || '').toLowerCase());
    return [...out].sort((a, b) => { const av = val(a); const bv = val(b); return av < bv ? (sort.direction === 'ascending' ? -1 : 1) : av > bv ? (sort.direction === 'ascending' ? 1 : -1) : 0; });
  }, [live, status, q, sort, byPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestSort = (key) => setSort((s) => ({ key, direction: s.key === key && s.direction === 'ascending' ? 'descending' : 'ascending' }));

  const visibleIds = rows.map((p) => p.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selectedIds.has(id));
  const selectedVisible = visibleIds.filter((id) => selectedIds.has(id));
  const toggleAll = () => setSelectedIds((prev) => { const n = new Set(prev); if (allSelected) visibleIds.forEach((id) => n.delete(id)); else visibleIds.forEach((id) => n.add(id)); return n; });
  const toggleOne = (id) => setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clearSelection = () => setSelectedIds(new Set());

  const onChange = async (positionId, chartOfAccountId) => {
    if (!locationId || !orgId) return;
    const existing = byPosition.get(positionId);
    try {
      if (existing && chartOfAccountId != null) {
        await updateLaborCoaMapping(existing.id, chartOfAccountId);
        setMappings((prev) => prev.map((m) => (m.id === existing.id ? { ...m, chartOfAccountId } : m)));
        toast.success(`GL account updated · ${acctLabel(chartOfAccountId)}`);
      } else if (existing && chartOfAccountId == null) {
        await deleteLaborCoaMapping(existing.id);
        setMappings((prev) => prev.filter((m) => m.id !== existing.id));
        toast.success('GL account cleared');
      } else if (!existing && chartOfAccountId != null) {
        const created = await createLaborCoaMapping({ orgId, locationId, positionId, chartOfAccountId });
        setMappings((prev) => [...prev, created]);
        toast.success(`GL account set · ${acctLabel(chartOfAccountId)}`);
      }
    } catch (err) {
      console.error('Error updating labor COA mapping:', err);
      toast.error('Could not save the GL account — try again');
    }
  };

  const onBulkApply = async (value) => {
    const chartOfAccountId = value == null ? null : Number(value);
    if (chartOfAccountId == null || selectedVisible.length === 0) return;
    setBulkApplying(true);
    try {
      const res = await bulkUpsertLaborCoaMappings(orgId, locationId, selectedVisible.map((positionId) => ({ positionId, chartOfAccountId })));
      if (res?.mappings) setMappings(res.mappings);
      toast.success(`Set ${acctLabel(chartOfAccountId)} on ${selectedVisible.length} position${selectedVisible.length !== 1 ? 's' : ''}`);
      clearSelection();
    } catch (err) {
      console.error('Bulk labor mapping failed:', err);
      toast.error('Could not set the GL account on the selected positions — nothing was changed');
    } finally {
      setBulkApplying(false);
    }
  };

  if (!locationId) {
    return (
      <div className="it-frame">
        <ConfigEmptyState
          title="Choose a location"
          body="Labor GL mappings are per location because each property has its own chart of accounts. Pick one in the Location selector above."
        />
      </div>
    );
  }

  const th = (key, label, style) => (
    <th className="it-sort" style={style} onClick={() => requestSort(key)}>{label} <SortIcon columnKey={key} sortKey={sort.key} direction={sort.direction} /></th>
  );

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: q, onChange: setQ, placeholder: 'Search position, department, account…' }}
        status={{ value: status, counts, onChange: setStatus }}
        meta={loading ? 'Loading…' : `${counts.mapped} / ${counts.all} mapped at ${locationName || 'this location'}`}
        actions={(
          <ToolbarButton variant="ai" icon={Zap} onClick={onOpenAi} disabled={loading || live.length === 0 || accounts.length === 0} title={accounts.length === 0 ? 'No expense accounts at this location' : 'Let AI suggest GL accounts for the positions'}>
            AI map positions
          </ToolbarButton>
        )}
      />
      {selectedVisible.length > 0 && (
        <BulkActionBar
          count={selectedVisible.length}
          noun={`position${selectedVisible.length !== 1 ? 's' : ''} selected`}
          actionLabel="Set GL account for selected:"
          options={accountOptions}
          placeholder="Choose account…"
          applying={bulkApplying}
          onApply={onBulkApply}
          onClear={clearSelection}
        />
      )}
      {!loading && live.length === 0 ? (
        <ConfigEmptyState title="No positions yet" body="Positions arrive from the payroll feed. Once a payroll file lands they appear here for GL mapping." />
      ) : !loading && rows.length === 0 ? (
        <ConfigEmptyState title="No positions match" body="Try clearing the search or the status filter." action={<ToolbarButton variant="ghost" onClick={() => { setQ(''); setStatus('all'); }}>Clear filters</ToolbarButton>} />
      ) : (
        <div className="it-scroll">
          <table className="it-tbl" style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ width: 44 }}>
                  <InlineCheckbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} disabled={visibleIds.length === 0} title={`Select all ${visibleIds.length} visible positions`} />
                </th>
                {th('position', 'Position')}
                {th('department', 'Department', { minWidth: 160 })}
                {th('account', 'GL expense account', { minWidth: 340 })}
                <th style={{ width: 56, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <LaborRow
                  key={p.id}
                  position={p}
                  departmentName={deptName(p.departmentId)}
                  accountOptions={accountOptions}
                  accountLandsIn={accountLandsIn}
                  mapping={byPosition.get(p.id)}
                  selected={selectedIds.has(p.id)}
                  onToggle={toggleOne}
                  onChange={(v) => onChange(p.id, v ?? null)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
